/**
 * gameStateHelpers.js — Tworzy plain JS obiekty stanu gry z metodami
 * które normalnie byłyby metodami instancji Mongoose
 */

import { shuffle } from './utils.js';

export function createPlayerState({
  userId,
  username,
  isBot = false,
  difficulty = 'medium',
  heroCard = null,
  faction = null,
  starterCards = [],
}) {
  const deck = shuffle([...starterCards]);
  return {
    userId,
    username,
    isBot,
    difficulty,
    heroCard,
    faction,
    fame: 0,
    tokens: { fury: 0, power: 0, parts: 0, knowledge: 0 },
    resources: { strength: 0, knowledge: 0, gold: 0 },
    pool: { strength: 0, knowledge: 0, gold: 0 },
    hand: [],
    deck,
    discard: [],
    played: [],
    destroyed: [],
    // Ile pozycji `destroyed` rozliczył już ArtifactSalvage (rekompensata za Artefakty)
    artifactSalvageCursor: 0,
    trophies: [],
    artifacts: [],
    equippedArtifacts: { pancerz: null, bron: null, amulet: null },
    artifactsUsedThisTurn: [],
    shields: 0,
    handSize: 5,
    mainActionsRemaining: 2,
    bonusActionsThisTurn: 0,
    purchasesRemaining: 1,
    challengesRemaining: 1,
    extraPurchases: 0,
    extraChallenges: 0,
    heroAbilityUsedThisTurn: false,
    strongMonstersDefeatedThisTurn: 0,
    purchasedThisTurn: false,
    cityZonesUsedThisTurn: {},
    pendingEffects: [],
    tempEffects: [],
    actionCount: 0,
    questsCompleted: 0,

    drawCards(count) {
      const drawn = [];
      for (let i = 0; i < count; i++) {
        if (this.deck.length === 0) {
          if (this.discard.length === 0) break;
          this.deck = shuffle([...this.discard]);
          this.discard = [];
        }
        const card = this.deck.shift();
        if (card) { this.hand.push(card); drawn.push(card); }
      }
      return drawn;
    },

    discardPlayed() {
      this.discard.push(...this.played);
      this.played = [];
    },

    discardHand() {
      this.discard.push(...this.hand);
      this.hand = [];
    },

    resetTurnCounters(gameConfig) {
      const defaultMain = gameConfig?.mainActionsPerTurn || 2;
      if (this.nextTurnMaxMainActions !== null && this.nextTurnMaxMainActions !== undefined) {
        this.mainActionsRemaining = Math.min(defaultMain, this.nextTurnMaxMainActions);
        this.nextTurnMaxMainActions = null;
      } else {
        this.mainActionsRemaining = defaultMain;
      }
      this.purchasesRemaining = 1 + (this.extraPurchases || 0);
      this.challengesRemaining = 1 + (this.extraChallenges || 0);
      this.extraPurchases = 0;
      this.extraChallenges = 0;
      this.bonusActionsThisTurn = 0;
      this.expeditionsCount = 0;
      this.artifactsUsedThisTurn = [];
      this.cityZonesUsedThisTurn = {};
      this.actionCount = 0;
      this.purchasedThisTurn = false;
      this.strongMonstersDefeatedThisTurn = 0;
      // Zniżka na Artefakt (Przemyt Towarów) obowiązuje tylko w turze, w której ją zdobyto —
      // tak samo jak w backendowym PlayerSchema.resetTurnCounters. Bez tego karta obiecywałaby
      // „W tej turze", a w trybie offline zniżka przechodziłaby na kolejne tury.
      this.artifactDiscount = 0;
    },

    resetHeroAbility() {
      this.heroAbilityUsedThisTurn = false;
    },

    clearExpiredEffects(currentTurn) {
      this.tempEffects = (this.tempEffects || []).filter(
        (e) => !e.expiresAfterTurn || e.expiresAfterTurn > currentTurn
      );
    },

    shuffleArray(arr) {
      return shuffle(arr);
    },
  };
}

export function createGameState({ code, players, gameConfig = {} }) {
  const config = {
    handSize: 5,
    bankResourceLimit: 10,
    marketplaceSize: 6,
    pathSize: 4,
    mainActionsPerTurn: 2,
    purchasesPerTurn: 1,
    challengesPerTurn: 1,
    epicChallengesForWin: 2,
    ...gameConfig,
  };

  return {
    code,
    gameId: typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    _id: typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    status: 'playing',
    hostId: players[0]?.userId || 'offline',
    maxPlayers: 6,
    currentPlayerIndex: 0,
    currentTurn: 1,
    currentPhase: 'ACTION',
    players,
    marketplace: [],
    pathOfCourage: [],
    pathOfWisdom: [],
    marketDeck: [],
    courageDeck: [],
    wisdomDeck: [],
    questDeck: [],
    guildContractDeck: [],
    revealedGuildContracts: [],
    cityZoneCards: [],
    eventLog: [],
    isTutorial: false,
    isSingleplayer: true,
    isOfflineGame: true,
    gameConfig: config,
    winner: null,
    finishedAt: null,
    pendingEvent: null,
    activeDurationEffects: [],
    wyprawaWCiemno: { active: false, drawnCourageIds: [], drawnWisdomIds: [], playerId: null },
    botActionLog: [],
    bossesDefeated: 0,
    epicQuestsCompleted: 0,

    getCurrentPlayer() {
      return this.players[this.currentPlayerIndex];
    },

    nextPlayer() {
      this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;
      if (this.currentPlayerIndex === 0) this.currentTurn++;
    },

    logEvent(userId, username, type, data) {
      // offline: log silently
      this.eventLog.push({ userId, username, type, data, timestamp: Date.now() });
    },

    markModified() {
      // no-op for offline (Mongoose compatibility)
    },

    refillAllZones() {
      // Puste sloty (null) po zdjętych kartach uzupełniamy DOKŁADNIE w ich miejscach —
      // karty na Szlaku i Targowisku nigdy się nie przesuwają. Dopiero potem dokładamy
      // brakujące pola na koniec. Spójne z backendowym Game.refillPathOf*.
      const fillInPlace = (zone, deck, size) => {
        for (let i = 0; i < zone.length; i++) {
          if (zone[i] || deck.length === 0) continue;
          zone[i] = deck.shift();
        }
        while (zone.length < size && deck.length > 0) zone.push(deck.shift());
      };
      fillInPlace(this.marketplace, this.marketDeck, this.gameConfig.marketplaceSize);
      fillInPlace(this.pathOfCourage, this.courageDeck, this.gameConfig.pathSize);
      fillInPlace(this.pathOfWisdom, this.wisdomDeck, this.gameConfig.pathSize);
    },

    checkGameEnd() {
      return (
        this.marketDeck.length === 0 &&
        this.courageDeck.length === 0 &&
        this.wisdomDeck.length === 0 &&
        this.marketplace.length === 0 &&
        this.pathOfCourage.length === 0 &&
        this.pathOfWisdom.length === 0
      );
    },

    calculateWinner() {
      if (!this.players.length) return;
      const sorted = [...this.players].sort((a, b) => (b.fame || 0) - (a.fame || 0));
      this.winner = sorted[0].userId;
      this.status = 'finished';
      this.finishedAt = Date.now();
    },

    clearExpiredDurationEffects() {
      return [];
    },

    revealGuildContracts(count = 3) {
      const toReveal = count - (this.revealedGuildContracts?.length || 0);
      for (let i = 0; i < toReveal && this.guildContractDeck?.length > 0; i++) {
        this.revealedGuildContracts.push(this.guildContractDeck.shift());
      }
    },
  };
}
